import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query'

import { getUserClient } from '@/api/user-client'
import {
  ErrorResponse,
  GetUsersActivityParams,
  GetUsersActivityResponse,
  IUser
} from '@/models'
import {
  buildUserIdBatches,
  toUserActivityMap,
  UserActivityMap
} from '@/utils/user-activity'

const userClient = getUserClient()

export const USER_QUERY_IDS = {
  GET_USER_BY_ID: 'GET_USER_BY_ID',
  GET_USER_TAGS: 'GET_USER_TAGS',
  GET_USERS: 'GET_USERS',
  GET_ORGANIZATION_USER: 'GET_ORGANIZATION_USER',
  GET_USER_DATA_POINTS: 'GET_USER_DATA_POINTS',
  GET_USERS_ACTIVITY: 'GET_USERS_ACTIVITY'
}

/**
 * Fetches last login / last activity for the given users, batched by 200 ids per request.
 * Results are keyed by `${org_id}:${user_id}`. Users without data are omitted.
 *
 * TODO: switch to `userClient.getUsersActivity(...)` once `@epilot/internal-user-client`
 * is republished with the `getUsersActivity` operation.
 */
export const fetchUsersActivity = async (
  userIds: (string | undefined | null)[],
  orgId?: string
): Promise<UserActivityMap> => {
  const batches = buildUserIdBatches(userIds)

  const responses = await Promise.all(
    batches.map((ids) => {
      const params: GetUsersActivityParams = {
        user_ids: ids.join(','),
        ...(orgId ? { org_id: orgId } : {})
      }

      return userClient.get<GetUsersActivityResponse>(
        '/v2/internal/users/activity',
        { params }
      )
    })
  )

  return toUserActivityMap(responses.flatMap((res) => res.data?.results ?? []))
}

export const useQueryGetUsersActivity = (
  userIds: (string | undefined | null)[],
  orgId?: string,
  opts?: Partial<UseQueryOptions<UserActivityMap>>
) => {
  const sortedIds = Array.from(
    new Set(userIds.filter((id): id is string => !!id))
  ).sort()

  return useQuery<UserActivityMap>({
    queryKey: [USER_QUERY_IDS.GET_USERS_ACTIVITY, orgId, sortedIds],
    queryFn: () => fetchUsersActivity(sortedIds, orgId),
    enabled: sortedIds.length > 0,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 60 * 1000,
    ...opts
  })
}

export const useQueryGetOrgUsers: (
  orgId: string,
  opts?: UseQueryOptions<Awaited<ReturnType<typeof userClient.listUsersV2>>>
) => ReturnType<
  typeof useQuery<Awaited<ReturnType<typeof userClient.listUsersV2>>>
> = (orgId, opts) => {
  return useQuery<Awaited<ReturnType<typeof userClient.listUsersV2>>>({
    queryKey: [USER_QUERY_IDS.GET_ORGANIZATION_USER, orgId],
    queryFn: () =>
      userClient.listUsersV2(
        { limit: 1000 },
        {},
        { headers: { 'x-ivy-org-id': orgId } }
      ),
    refetchOnWindowFocus: true,
    ...opts
  })
}

export const useQueryGetUserById: (
  userId: string,
  orgId: string,
  opts?: Partial<UseQueryOptions<IUser>>
) => ReturnType<typeof useQuery<IUser>> = (userId, orgId, opts) => {
  return useQuery<IUser>({
    queryKey: [USER_QUERY_IDS.GET_USER_BY_ID, userId, orgId],
    queryFn: () =>
      userClient
        .getUserV2({ id: userId }, {}, { headers: { 'x-ivy-org-id': orgId } })
        .then((res) => res.data),
    refetchOnWindowFocus: false,
    retry: false,
    ...opts
  })
}

export const useQueryGetUserTags: (
  opts?: UseQueryOptions<Awaited<ReturnType<typeof userClient.getUserTags>>>
) => ReturnType<
  typeof useQuery<Awaited<ReturnType<typeof userClient.getUserTags>>>
> = (opts) => {
  return useQuery<Awaited<ReturnType<typeof userClient.getUserTags>>>({
    queryKey: [USER_QUERY_IDS.GET_USER_TAGS],
    queryFn: () => userClient.getUserTags(),
    refetchOnWindowFocus: false,
    retry: false,
    ...opts
  })
}

export const useQueryGetUserDataPoints: (
  opts?: UseQueryOptions<Awaited<ReturnType<typeof userClient.getDataPoints>>>
) => ReturnType<
  typeof useQuery<Awaited<ReturnType<typeof userClient.getDataPoints>>>
> = (opts) => {
  return useQuery<Awaited<ReturnType<typeof userClient.getDataPoints>>>({
    queryKey: [USER_QUERY_IDS.GET_USER_DATA_POINTS],
    queryFn: () => userClient.getDataPoints(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    ...opts
  })
}

export const useMutationUpdateUser: (
  orgId: string
) => ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof userClient.updateUserV2>>,
    ErrorResponse,
    IUser,
    unknown
  >
> = (orgId) => {
  return useMutation<
    Awaited<ReturnType<typeof userClient.updateUserV2>>,
    ErrorResponse,
    IUser,
    unknown
  >({
    mutationFn: (user: IUser) => {
      return userClient.updateUserV2(user.id, user, {
        headers: {
          'x-ivy-org-id': orgId
        }
      })
    }
  })
}

export const useMutationDeleteUser: (
  orgId: string
) => ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof userClient.deleteUserV2>>,
    ErrorResponse,
    string,
    unknown
  >
> = (orgId) => {
  const queryClient = useQueryClient()

  return useMutation<
    Awaited<ReturnType<typeof userClient.deleteUserV2>>,
    ErrorResponse,
    string,
    unknown
  >({
    mutationFn: (userId: string) => {
      return userClient.deleteUserV2({ id: userId }, null, {
        headers: {
          'x-ivy-org-id': orgId
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_IDS.GET_USERS]
      })
    }
  })
}

export const useMutationUpdateUserEmail: (
  orgId: string
) => ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof userClient.changeUserEmail>>,
    ErrorResponse,
    { oldEmail: string; newEmail: string },
    unknown
  >
> = (orgId) => {
  return useMutation<
    Awaited<ReturnType<typeof userClient.changeUserEmail>>,
    ErrorResponse,
    { oldEmail: string; newEmail: string },
    unknown
  >({
    mutationFn: ({
      oldEmail,
      newEmail
    }: {
      oldEmail: string
      newEmail: string
    }) => {
      return userClient.changeUserEmail(
        { username: oldEmail },
        { new_email: newEmail },
        {
          headers: {
            'x-ivy-org-id': orgId
          }
        }
      )
    }
  })
}

export const useMutationSendPasswordReset: (
  orgId: string
) => ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof userClient.sendUserPasswordReset>>,
    ErrorResponse,
    { email: string },
    unknown
  >
> = (orgId) => {
  return useMutation<
    Awaited<ReturnType<typeof userClient.sendUserPasswordReset>>,
    ErrorResponse,
    { email: string },
    unknown
  >({
    mutationFn: ({ email }: { email: string }) => {
      return userClient.sendUserPasswordReset(
        null,
        { email },
        {
          headers: {
            'x-ivy-org-id': orgId
          }
        }
      )
    }
  })
}

export const useMutationChangeUserPrimaryOrg: (
  orgId: string
) => ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof userClient.changeUserPrimaryOrg>>,
    ErrorResponse,
    { username: string; primary_org_id: string },
    unknown
  >
> = (orgId) => {
  return useMutation<
    Awaited<ReturnType<typeof userClient.changeUserPrimaryOrg>>,
    ErrorResponse,
    { username: string; primary_org_id: string },
    unknown
  >({
    mutationFn: ({
      username,
      primary_org_id
    }: {
      username: string
      primary_org_id: string
    }) => {
      return userClient.changeUserPrimaryOrg(
        { username },
        { primary_org_id },
        {
          headers: {
            'x-ivy-org-id': orgId
          }
        }
      )
    }
  })
}
